import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronRight, ChevronLeft, TrendingUp, Users, Trophy, Medal, Award, Home, Trees, Layers, AlertCircle, Plus, Trash2, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const fmt = (v) => v?.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' }) ?? '0 $';
const pct = (v) => `${(v ?? 0).toFixed(1)}%`;

function CouleurBadge({ couleur, pctVal }) {
  if (couleur === 'vert') return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-800">
      🟢 {pct(pctVal)}
    </span>
  );
  if (couleur === 'orange') return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-sm font-semibold bg-amber-100 text-amber-800">
      🟠 {pct(pctVal)}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-sm font-semibold bg-red-100 text-red-800">
      🔴 {pct(pctVal)}
    </span>
  );
}

function MedalIcon({ rank }) {
  if (rank === 0) return <span className="text-2xl">🥇</span>;
  if (rank === 1) return <span className="text-2xl">🥈</span>;
  if (rank === 2) return <span className="text-2xl">🥉</span>;
  return <span className="text-lg text-gray-400 font-bold">#{rank + 1}</span>;
}

function StatBar({ vert, orange, rouge, total }) {
  if (total === 0) return null;
  return (
    <div className="space-y-1">
      <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
        <div className="bg-emerald-500 h-full transition-all" style={{ width: `${(vert / total) * 100}%` }} />
        <div className="bg-amber-400 h-full transition-all" style={{ width: `${(orange / total) * 100}%` }} />
        <div className="bg-red-500 h-full transition-all" style={{ width: `${(rouge / total) * 100}%` }} />
      </div>
      <div className="flex gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />{vert} vert</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />{orange} orange</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />{rouge} rouge</span>
      </div>
    </div>
  );
}

export const AnalyseMarges = () => {
  const { isProprietaire } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projets, setProjets] = useState([]);
  const [seuils, setSeuils] = useState({ vert: 40, orange: 30 });
  const [view, setView] = useState('global'); // 'global' | 'type' | 'categorie'
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCategorie, setSelectedCategorie] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);

  const loadCategories = () => {
    axios.get(`${API_URL}/api/crm/categories-travaux`)
      .then(r => r.data)
      .then(d => setCategories(d.categories || []))
      .catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/crm/analyse-marges`)
      .then(r => r.data)
      .then(d => {
        setProjets(d.projets || []);
        if (d.seuils) setSeuils(d.seuils);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    loadCategories();
  }, []);

  const handleAddCategorie = async () => {
    const nom = newCat.trim();
    if (!nom) return;
    setAddingCat(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/categories-travaux`, { nom });
      const d = res.data;
      if (res.status === 200) { setCategories(prev => [...prev, d].sort((a, b) => a.nom.localeCompare(b.nom))); setNewCat(''); toast.success('Catégorie ajoutée'); }
      else toast.error(d.detail || 'Erreur');
    } catch { toast.error('Erreur réseau'); }
    finally { setAddingCat(false); }
  };

  const handleDeleteCategorie = async (cat) => {
    if (!window.confirm(`Supprimer la catégorie "${cat.nom}" ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/crm/categories-travaux/${cat.id}`);
      setCategories(prev => prev.filter(c => c.id !== cat.id));
      toast.success('Catégorie supprimée');
    } catch { toast.error('Erreur'); }
  };

  // Helper: couleur dynamique selon seuils configurés
  const couleurMarge = (pct) => pct >= seuils.vert ? 'emerald' : pct >= seuils.orange ? 'amber' : 'red';
  const textCouleurMarge = (pct) => pct >= seuils.vert ? 'text-emerald-600' : pct >= seuils.orange ? 'text-amber-600' : 'text-red-600';

  // ---- Helpers ----
  const groupBy = (arr, key) => arr.reduce((acc, item) => {
    const k = item[key] || 'Non classifié';
    acc[k] = acc[k] || [];
    acc[k].push(item);
    return acc;
  }, {});

  const statsGroup = (items) => {
    const total = items.length;
    const vert = items.filter(p => p.couleur === 'vert').length;
    const orange = items.filter(p => p.couleur === 'orange').length;
    const rouge = items.filter(p => p.couleur === 'rouge').length;
    const marge_moy = total > 0 ? items.reduce((s, p) => s + p.marge_pct, 0) / total : 0;
    const ca_total = items.reduce((s, p) => s + p.montant_contrat, 0);
    return { total, vert, orange, rouge, marge_moy, ca_total };
  };

  const TYPE_LABELS = { interieur: '🏠 Intérieur', exterieur: '🏡 Extérieur', mixte: '🏠🏡 Mixte', null: '❓ Non classifié' };
  const TYPE_COLORS = {
    interieur: 'border-blue-300 bg-blue-50 hover:bg-blue-100',
    exterieur: 'border-green-300 bg-green-50 hover:bg-green-100',
    mixte: 'border-purple-300 bg-purple-50 hover:bg-purple-100',
  };

  // ---- Views ----

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
    </div>
  );

  if (projets.length === 0) return (
    <div className="space-y-6">
      {/* Gestion des catégories — même en l'absence de projets finalisés */}
      {isProprietaire && (
        <Card className="border-2 border-blue-100 bg-blue-50/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Tag className="w-4 h-4 text-blue-600" /> Gestion des catégories de projets</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowCatManager(v => !v)}>
                {showCatManager ? 'Masquer' : 'Gérer'}
              </Button>
            </div>
          </CardHeader>
          {showCatManager && (
            <CardContent>
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Nouvelle catégorie (ex: Salon, Patio, Garde-corps...)"
                  value={newCat}
                  onChange={e => setNewCat(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddCategorie()}
                  className="max-w-xs"
                  data-testid="new-cat-input"
                />
                <Button size="sm" onClick={handleAddCategorie} disabled={addingCat || !newCat.trim()}>
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map(c => (
                  <Badge key={c.id} variant="outline" className="text-sm py-1 px-2 flex items-center gap-1 bg-white">
                    {c.nom}
                    <button onClick={() => handleDeleteCategorie(c)} className="text-red-400 hover:text-red-600 ml-1"><Trash2 className="w-3 h-3" /></button>
                  </Badge>
                ))}
                {categories.length === 0 && <p className="text-xs text-gray-400">Créez vos premières catégories ici. Elles seront disponibles dans l'onglet Stats de chaque dossier.</p>}
              </div>
            </CardContent>
          )}
        </Card>
      )}
      <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
        <TrendingUp className="w-8 h-8 text-emerald-600" /> Analyse des Marges Clients
      </h1>
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
          <div>
            <p className="font-semibold text-amber-900">Aucun projet terminé trouvé</p>
            <p className="text-sm text-amber-700 mt-1">
              Les projets apparaissent ici une fois leur statut passé à <strong>Finalisé</strong> ou <strong>Fermé</strong>.
              Assurez-vous également d'avoir défini le <strong>Type de travaux</strong> et la <strong>Catégorie</strong> dans l'onglet Stats de chaque dossier.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ---- VUE GLOBALE ----
  const globalStats = statsGroup(projets);
  const byType = groupBy(projets, 'type_travaux');

  // ---- VUE PAR TYPE (intérieur / extérieur / mixte) ----
  const projetsType = selectedType ? projets.filter(p => (p.type_travaux || 'Non classifié') === selectedType) : [];
  const byCategorie = groupBy(projetsType, 'categorie_travaux');
  const categorieStats = Object.entries(byCategorie).map(([cat, items]) => ({
    cat,
    ...statsGroup(items),
    items,
  })).sort((a, b) => b.marge_moy - a.marge_moy);

  // ---- VUE PAR CATÉGORIE ----
  const projetsCat = selectedCategorie
    ? projetsType.filter(p => (p.categorie_travaux || 'Non classifié') === selectedCategorie)
    : [];

  return (
    <div className="space-y-6">
      {/* Gestion des catégories — Propriétaire seulement */}
      {isProprietaire && (
        <Card className="border-2 border-blue-100 bg-blue-50/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Tag className="w-4 h-4 text-blue-600" /> Gestion des catégories de projets</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowCatManager(v => !v)}>
                {showCatManager ? 'Masquer' : 'Gérer'}
              </Button>
            </div>
          </CardHeader>
          {showCatManager && (
            <CardContent>
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Nouvelle catégorie (ex: Salon, Patio, Garde-corps...)"
                  value={newCat}
                  onChange={e => setNewCat(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddCategorie()}
                  className="max-w-xs"
                  data-testid="new-cat-input"
                />
                <Button size="sm" onClick={handleAddCategorie} disabled={addingCat || !newCat.trim()}>
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map(c => (
                  <Badge key={c.id} variant="outline" className="text-sm py-1 px-2 flex items-center gap-1 bg-white">
                    {c.nom}
                    <button onClick={() => handleDeleteCategorie(c)} className="text-red-400 hover:text-red-600 ml-1"><Trash2 className="w-3 h-3" /></button>
                  </Badge>
                ))}
                {categories.length === 0 && <p className="text-xs text-gray-400">Aucune catégorie créée. Les catégories seront disponibles dans l'onglet Stats de chaque dossier.</p>}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <TrendingUp className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-600" /> Analyse des Marges Clients
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Seuils : 🟢 ≥{seuils.vert}% · 🟠 {seuils.orange}–{seuils.vert - 0.1}% · 🔴 &lt; {seuils.orange}%
          </p>
        </div>
        {/* Fil d'Ariane */}
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <button onClick={() => { setView('global'); setSelectedType(null); setSelectedCategorie(null); }}
            className={`hover:text-emerald-700 font-medium ${view === 'global' ? 'text-emerald-700' : ''}`}>
            Vue globale
          </button>
          {selectedType && <>
            <ChevronRight className="w-4 h-4" />
            <button onClick={() => { setView('type'); setSelectedCategorie(null); }}
              className={`hover:text-emerald-700 font-medium ${view === 'type' ? 'text-emerald-700' : ''}`}>
              {TYPE_LABELS[selectedType]}
            </button>
          </>}
          {selectedCategorie && <>
            <ChevronRight className="w-4 h-4" />
            <span className="text-emerald-700 font-medium">{selectedCategorie}</span>
          </>}
        </div>
      </div>

      {/* ===== VUE GLOBALE ===== */}
      {view === 'global' && (
        <div className="space-y-6">
          {/* KPIs globaux */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Projets terminés</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{globalStats.total}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Marge moyenne</p>
              <p className={`text-3xl font-bold mt-1 ${textCouleurMarge(globalStats.marge_moy)}`}>
                {pct(globalStats.marge_moy)}
              </p>
            </CardContent></Card>
            <Card><CardContent className="pt-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide">CA total analysé</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{fmt(globalStats.ca_total)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Distribution</p>
              <div className="mt-2">
                <StatBar {...globalStats} />
              </div>
            </CardContent></Card>
          </div>

          {/* Tuiles par type */}
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Par type de travaux — cliquez pour explorer</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(byType).map(([type, items]) => {
                const s = statsGroup(items);
                return (
                  <button key={type} onClick={() => { setSelectedType(type); setView('type'); }}
                    className={`text-left p-5 rounded-xl border-2 transition-all cursor-pointer ${TYPE_COLORS[type] || 'border-gray-200 bg-gray-50 hover:bg-gray-100'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xl font-bold">{TYPE_LABELS[type] || type}</span>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className={`text-3xl font-bold ${textCouleurMarge(s.marge_moy)}`}>
                        {pct(s.marge_moy)}
                      </span>
                      <span className="text-sm text-gray-500">marge moy. · {s.total} projets</span>
                    </div>
                    <StatBar {...s} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tous les projets */}
          <Card>
            <CardHeader><CardTitle>Tous les projets terminés</CardTitle></CardHeader>
            <CardContent>
              <ProjetTable projets={projets} />
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== VUE PAR TYPE (catégories + médailles) ===== */}
      {view === 'type' && (
        <div className="space-y-6">
          <button onClick={() => { setView('global'); setSelectedType(null); }}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-emerald-700">
            <ChevronLeft className="w-4 h-4" /> Retour vue globale
          </button>

          {/* Stats du type */}
          {(() => {
            const s = statsGroup(projetsType);
            return (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card><CardContent className="pt-5">
                  <p className="text-xs text-gray-500">Projets {TYPE_LABELS[selectedType]}</p>
                  <p className="text-3xl font-bold mt-1">{s.total}</p>
                </CardContent></Card>
                <Card><CardContent className="pt-5">
                  <p className="text-xs text-gray-500">Marge moyenne</p>
                  <p className={`text-3xl font-bold mt-1 ${textCouleurMarge(s.marge_moy)}`}>{pct(s.marge_moy)}</p>
                </CardContent></Card>
                <Card><CardContent className="pt-5">
                  <p className="text-xs text-gray-500">Distribution</p>
                  <div className="mt-2"><StatBar {...s} /></div>
                </CardContent></Card>
              </div>
            );
          })()}

          {/* Podium des 3 meilleures catégories */}
          {categorieStats.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-3">
                🏆 Catégories les plus rentables
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {categorieStats.slice(0, 3).map((cs, i) => (
                  <button key={cs.cat}
                    onClick={() => { setSelectedCategorie(cs.cat); setView('categorie'); }}
                    className={`text-left p-5 rounded-xl border-2 transition-all hover:shadow-md ${
                      i === 0 ? 'border-yellow-400 bg-yellow-50 hover:bg-yellow-100' :
                      i === 1 ? 'border-gray-300 bg-gray-50 hover:bg-gray-100' :
                      'border-amber-600 bg-orange-50 hover:bg-orange-100'
                    }`}>
                    <div className="flex items-center justify-between mb-2">
                      <MedalIcon rank={i} />
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="font-bold text-lg text-slate-900">{cs.cat}</p>
                    <p className={`text-2xl font-bold mt-1 ${textCouleurMarge(cs.marge_moy)}`}>
                      {pct(cs.marge_moy)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{cs.total} projet{cs.total > 1 ? 's' : ''} · {fmt(cs.ca_total)}</p>
                    <div className="mt-2"><StatBar {...cs} /></div>
                  </button>
                ))}
              </div>

              {/* Toutes les catégories */}
              {categorieStats.length > 3 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 mb-2">Autres catégories</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {categorieStats.slice(3).map((cs, i) => (
                      <button key={cs.cat}
                        onClick={() => { setSelectedCategorie(cs.cat); setView('categorie'); }}
                        className="text-left p-4 rounded-lg border border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50 transition-all flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <MedalIcon rank={i + 3} />
                            <span className="font-semibold text-slate-800">{cs.cat}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{cs.total} projet{cs.total > 1 ? 's' : ''} · {fmt(cs.ca_total)}</p>
                        </div>
                        <div className="text-right">
                          <CouleurBadge couleur={couleurMarge(cs.marge_moy)} pctVal={cs.marge_moy} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Projets sans catégorie */}
          <Card>
            <CardHeader><CardTitle>Projets {TYPE_LABELS[selectedType]}</CardTitle></CardHeader>
            <CardContent><ProjetTable projets={projetsType} /></CardContent>
          </Card>
        </div>
      )}

      {/* ===== VUE PAR CATÉGORIE ===== */}
      {view === 'categorie' && (
        <div className="space-y-6">
          <button onClick={() => { setView('type'); setSelectedCategorie(null); }}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-emerald-700">
            <ChevronLeft className="w-4 h-4" /> Retour {TYPE_LABELS[selectedType]}
          </button>

          {(() => {
            const s = statsGroup(projetsCat);
            const rank = categorieStats.findIndex(c => c.cat === selectedCategorie);
            return (
              <div className="flex items-center gap-4">
                {rank < 3 && <MedalIcon rank={rank} />}
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{selectedCategorie}</h2>
                  <p className="text-gray-500">{TYPE_LABELS[selectedType]} · {s.total} projets · marge moy. <span className={`font-bold ${textCouleurMarge(s.marge_moy)}`}>{pct(s.marge_moy)}</span></p>
                </div>
              </div>
            );
          })()}

          <Card>
            <CardContent className="pt-4">
              <ProjetTable projets={projetsCat} showDetail />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

function ProjetTable({ projets, showDetail = false }) {
  const fmt = (v) => v?.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' }) ?? '0 $';
  const sorted = [...projets].sort((a, b) => b.marge_pct - a.marge_pct);

  if (sorted.length === 0) return <p className="text-sm text-gray-400 py-4 text-center">Aucun projet</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-xs text-gray-500 uppercase">
            <th className="text-left py-2 pr-4">Client</th>
            <th className="text-left py-2 pr-4">Dossier</th>
            {!showDetail && <th className="text-left py-2 pr-4">Type</th>}
            {!showDetail && <th className="text-left py-2 pr-4">Catégorie</th>}
            <th className="text-right py-2 pr-4">Contrat</th>
            {showDetail && <>
              <th className="text-right py-2 pr-4">M.O.</th>
              <th className="text-right py-2 pr-4">Produits</th>
              <th className="text-right py-2 pr-4">Matériaux</th>
            </>}
            <th className="text-right py-2">Marge</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(p => (
            <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50">
              <td className="py-2 pr-4 font-medium">{p.client_nom}</td>
              <td className="py-2 pr-4 text-gray-600">{p.nom_dossier}</td>
              {!showDetail && <td className="py-2 pr-4 text-gray-500 text-xs">{p.type_travaux || '—'}</td>}
              {!showDetail && <td className="py-2 pr-4 text-gray-500 text-xs">{p.categorie_travaux || '—'}</td>}
              <td className="py-2 pr-4 text-right font-semibold">{fmt(p.montant_contrat)}</td>
              {showDetail && <>
                <td className="py-2 pr-4 text-right text-gray-500">{fmt(p.cout_mo)}</td>
                <td className="py-2 pr-4 text-right text-gray-500">{fmt(p.cout_produits)}</td>
                <td className="py-2 pr-4 text-right text-gray-500">{fmt(p.cout_materiaux)}</td>
              </>}
              <td className="py-2 text-right">
                <CouleurBadge couleur={p.couleur} pctVal={p.marge_pct} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AnalyseMarges;
